/**
 * useBulkMisData Hook
 *
 * Custom hook for fetching bulk MIS dashboard data
 */

import { useState, useEffect, useCallback } from 'react';
import { bulkMisAPI } from '../../../../services/bulk-mis-api.service';
import { useErrorHandler } from '../../../../hooks/useErrorHandler';
import type {
  BulkMisAPIResponse,
  BulkMisFilters,
  GetBulkBatchesParams,
} from '../../../../types/bulk-mis';

interface UseBulkMisDataReturn {
  data: BulkMisAPIResponse['data'] | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useBulkMisData(
  filters: BulkMisFilters,
  page: number,
  pageSize: number
): UseBulkMisDataReturn {
  const [data, setData] = useState<BulkMisAPIResponse['data'] | null>(null);
  // Note: Initial loading state is true because fetchData is called immediately via useEffect.
  // This ensures consuming components show the loading state on first render.
  // If this hook is used conditionally or fetchData is guarded, loading may need to be reset.
  const [loading, setLoading] = useState(true);
  const { handleError, errorMessage, clearError } = useErrorHandler();

  const fetchData = useCallback(async () => {
    setLoading(true);
    clearError();

    try {
      const params: GetBulkBatchesParams = {
        page,
        limit: pageSize,
        sort_by: 'created_at',
        sort_order: 'desc',
      };

      // Add filters if set
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.pipelineSource && filters.pipelineSource !== 'all') {
        params.pipeline_source = filters.pipelineSource;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.batchType && filters.batchType !== 'all') {
        params.batch_type = filters.batchType;
      }

      if (filters.dateRange.from) {
        params.from_date = filters.dateRange.from.toISOString();
      }

      if (filters.dateRange.to) {
        params.to_date = filters.dateRange.to.toISOString();
      }

      const response = await bulkMisAPI.getBatches(params);

      setData(response.data);
    } catch (err) {
      console.error('[useBulkMisData] Error fetching data:', err);
      handleError(err, { component: 'useBulkMisData', action: 'fetchData' });
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, handleError, clearError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error: errorMessage,
    refresh: fetchData,
  };
}
